import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { rootReducer } from '@wellesley/store'
import { DatedMessage, WsSubscribeMessage } from '../types'
import {
  wsSubscribe,
  wsUnSubscribe,
  wsOnMessage,
} from '@wellesley/host-remote/api/actions'

type MatchConfig = {
  subscriptionName: string
  mode: 'startsWith' | 'equals' | 'contains'
}

const matchesTopic = (
  topic: string,
  { subscriptionName, mode }: MatchConfig,
): boolean => {
  switch (mode) {
    case 'startsWith':
      return topic.startsWith(subscriptionName)
    case 'equals':
      return topic === subscriptionName
    case 'contains':
      return topic.includes(subscriptionName)
    default:
      return false
  }
}

export type WsSliceState = {
  ws: WsState
}

export type WsState = {
  subscribers: WsSubscribeMessage[]
  unsubscribers: WsSubscribeMessage[]
  messages: DatedMessage[]
}

export const initialState: WsState = {
  messages: [],
  subscribers: [],
  unsubscribers: [],
}

const MAX_MESSAGES = 100

export const wsSlice = createSlice({
  name: 'ws',
  initialState,
  reducers: {
    reset: (state) => {
      state.messages = initialState.messages
      state.subscribers = initialState.subscribers
      state.unsubscribers = initialState.unsubscribers
    },

    cleanMessages: (state) => {
      state.messages = initialState.messages
    },

    cleanTopicMessages: (state, { payload }: PayloadAction<MatchConfig>) => {
      state.messages = state.messages.filter((message) => {
        return !matchesTopic(message.topic, payload)
      })
    },

    onUnsubscribe: (state, { payload: id }: PayloadAction<string>) => {
      state.subscribers = state.subscribers.filter((s) => s.id !== id)
      state.unsubscribers = state.unsubscribers.filter((s) => s.id !== id)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(wsSubscribe, (state, { payload }) => {
        const isAlreadyExist = state.subscribers.find(
          (s) => s.id === payload.id,
        )
        !isAlreadyExist && state.subscribers.push(payload)
      })
      .addCase(wsUnSubscribe, (state, { payload: id }) => {
        const subscription = state.subscribers.find((s) => s.id === id)
        subscription && state.unsubscribers.push(subscription)
      })
      .addCase(wsOnMessage, (state, { payload }) => {
        state.messages = [payload, ...state.messages.slice(0, MAX_MESSAGES - 1)]
      })
  },
  selectors: {
    selectMessages: (state) => state.messages,
    selectSubscribers: (state) => state.subscribers,
    selectUnSubscribers: (state) => state.unsubscribers,
    selectLastUpdateFor: (state, matchConfig: MatchConfig) =>
      state.messages?.find(({ topic }) => matchesTopic(topic, matchConfig)),
  },
}).injectInto(rootReducer)
